import { Time } from "@internationalized/date";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { useTimeFormatter } from "@trip/react-hooks";

import { AngleDownIcon } from "~/components/icons/angle-down";
import { ClockIcon } from "~/components/icons/clock-icon";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { type RouterOutputs } from "~/trpc/types";

export type StartTimeFieldValue =
  RouterOutputs["tours"]["choices"][0]["startTimes"][0] & {
    disabled?: boolean;
  };

function isDisabled(startTime: StartTimeFieldValue) {
  if (typeof startTime.disabled === "boolean" && startTime.disabled) {
    return true;
  }
  if (typeof startTime.totalQuantityAvailable === "number") {
    return startTime.totalQuantityAvailable === 0;
  }
  return false;
}

export function StartTimesPicker({
  startTimes,
  onChange,
  value,
}: {
  onChange: (value: string) => void;
  startTimes: StartTimeFieldValue[];
  value: string | null;
}) {
  const timeFormatter = useTimeFormatter();
  return (
    <RadioGroup
      value={value ?? ""}
      onValueChange={(value) => {
        onChange(value);
      }}
      className="grid-cols-3 text-sm"
    >
      {startTimes.map((startTime) => (
        <RadioGroupItem
          key={startTime.id}
          value={startTime.id ?? ""}
          disabled={isDisabled(startTime)}
        >
          {timeFormatter(new Time(startTime.hour, startTime.minute))}
        </RadioGroupItem>
      ))}
    </RadioGroup>
  );
}

export function PopoverStartTimesPicker({
  startTimes,
  onChange,
  value,
  disabled,
}: {
  onChange: (value: string) => void;
  startTimes: StartTimeFieldValue[];
  value: string | null;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timeFormatter = useTimeFormatter();
  const startTimeValue =
    startTimes.find(({ id }) => id === value) ?? startTimes[0];
  const t = useTranslations("TourDetails");
  const label = startTimeValue
    ? timeFormatter(new Time(startTimeValue.hour, startTimeValue.minute))
    : "Pick Time";

  const enabledStartTimes = startTimes.filter(
    (startTime) => !isDisabled(startTime),
  );

  if (enabledStartTimes.length === 1) {
    return (
      <Button
        disabled
        className="w-full justify-between disabled:text-neutral-900"
      >
        <div className="flex items-center gap-2">
          <ClockIcon />
          {label}
        </div>
      </Button>
    );
  }

  return (
    <Popover
      onOpenChange={(open) => {
        setOpen(open);
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <Button className="w-full justify-between gap-2" disabled={disabled}>
          <div className="flex items-center gap-2">
            <ClockIcon />
            {label}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-prune-300 font-normal">
              {typeof startTimeValue?.totalQuantityAvailable === "number" &&
              startTimeValue.totalQuantityAvailable < 6
                ? t("ticketsAvailable", {
                    availableQuantity: startTimeValue.totalQuantityAvailable,
                  })
                : null}
            </span>
            {startTimes.length > 1 ? (
              <AngleDownIcon className={open ? "rotate-180" : ""} />
            ) : null}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="shadow-card-search w-auto rounded-2xl border-none p-3 tabular-nums"
        sideOffset={8}
      >
        <StartTimesPicker
          onChange={(value) => {
            onChange(value);
            setOpen(false);
          }}
          startTimes={startTimes}
          value={value}
        />
      </PopoverContent>
    </Popover>
  );
}
